import { ArgumentError } from "../core/exception/argument-error";
import { KeyNotExistsError } from "../core/exception/key-not-exists-error";
import { Bit } from "./bit";
import { NullReferenceError } from "../core/exception/null-reference-error";
import { InvalidParameterError } from "../core/exception/invalid-parameter-error";
import { IGroupBy } from "../types/interfaces";

export enum Order {
	None = 0,
	Ascendend = 1,
	Descendend = 2
}

export abstract class ArrayHelper {
	private static chachNumbers: number[] = [];

	public static removeElementFromArrayOneInArrayTwo(outerArr: any[], innerArr: any[], param: string = ""): void {
		let result: any;
		for (let i = 0; i < outerArr.length; i++) {
			let value = outerArr[i];
			if (param !== "") {
				result = innerArr.indexOf(value[param]);
			} else {
				result = innerArr.indexOf(value);
			}
			if (result === -1) {
				outerArr.splice(i, 1);
			}
		}
	}

	public static concat(firstArr: any[], secondArr: any[]): any[] {
		firstArr = firstArr ?? [];
		secondArr = secondArr ?? [];
		return firstArr.concat(secondArr);
	}

	public static addAndRemoveElementInDynamicArray(array: number[]) {
		let result: any;
		for (let value of array) {
			result = ArrayHelper.chachNumbers.indexOf(value);
			if (result === -1) {
				ArrayHelper.chachNumbers.push(value);
			}
		}

		if (ArrayHelper.chachNumbers.length !== array.length) {
			for (let i = 0; i < ArrayHelper.chachNumbers.length; i++) {
				let value = ArrayHelper.chachNumbers[i];
				result = array.indexOf(value);
				if (result === -1) {
					ArrayHelper.chachNumbers.splice(i, 1);
				}
			}
		}

		return ArrayHelper.chachNumbers;
	}

	/**
	 * Returns an array with the items in source but not in compare
	 * @param arrSource source to compare from
	 * @param arrCompare compare to compare against
	 */
	public static diff<T>(arrSource: T[], arrCompare: T[], distinct?: boolean) {
		if (!arrSource) {
			return [];
		}
		if (!arrCompare) {
			return arrSource;
		}
		let distinctResult: T[] = [];
		let result = arrSource.filter((val) => {
			let hasIndex = arrCompare.indexOf(val) === -1;
			if (distinct && hasIndex && distinctResult.indexOf(val) === -1) {
				distinctResult.push(val);
			}
			return hasIndex;
		});

		return distinct ? distinctResult : result;
	}

	public static intersect<T>(arrSource: T[], arrCompare: T[]) {
		if (!arrSource || !arrCompare || arrSource.length === 0 || arrCompare.length === 0) {
			return [];
		}

		let diffSource = ArrayHelper.diff(arrSource, arrCompare);
		let diffCompare = ArrayHelper.diff(arrCompare, arrSource);

		let all = arrSource.concat(arrCompare);
		let ignore = diffSource.concat(diffCompare);

		return ArrayHelper.diff(arrSource.concat(arrCompare), diffSource.concat(diffCompare), true);
	}

	/**
	 * Indicates if two arrays are equal
	 * @param arr1 first array
	 * @param arr2 second array
	 */
	public static equals<T>(arr1: T[], arr2: T[]): boolean {
		if (arr1 === arr2) {
			return true;
		}
		let diff1 = this.diff(arr1, arr2);
		let diff2 = this.diff(arr2, arr1);

		if (diff1 === diff2 || (diff1.length === 0 && diff2.length === 0)) {
			return true;
		}

		return false;
	}

	public static same<T>(arr1: T[], arr2: T[]): boolean {
		let equalResult = ArrayHelper.equals(arr1, arr2);
		if (equalResult) {
			return true;
		}

		if (!((arr1?.length) > 0) || !(arr2?.length > 0)) {
			return equalResult;
		}

		if (arr1?.length !== arr2?.length) {
			return false;
		}

		let flattenObject = (ob: any) => {
			let toReturn: any = {};
			for (let i in ob) {
				if (!ob.hasOwnProperty(i)) continue;

				if ((typeof ob[i]) === 'object') {
					let flatObject = flattenObject(ob[i]);
					for (let x in flatObject) {
						if (!flatObject.hasOwnProperty(x)) continue;
						toReturn[i + '.' + x] = flatObject[x];
					}
				} else {
					toReturn[i] = ob[i];
				}
			}
			return toReturn;
		};

		let myFlattenedObj1 = flattenObject(arr1);
		let parseArr1 = JSON.stringify(myFlattenedObj1, Object.keys(myFlattenedObj1).sort());

		let myFlattenedObj2 = flattenObject(arr2);
		let parseArr2 = JSON.stringify(myFlattenedObj2, Object.keys(myFlattenedObj2).sort());

		return parseArr1 === parseArr2;
	}

	public static createArrayWithItem<TLength extends number>(len: TLength, item: any): any[] & { length: TLength } {
		let arr1: any[] = [item];
		let arr2: any[] = [];
		let lenNum = len as number;
		while (lenNum > 0) {
			if (Bit.and(lenNum, 1)) {
				arr2 = arr2.concat(arr1);
			}
			arr1 = arr1.concat(arr1);
			// tslint:disable-next-line
			lenNum >>>= 1; // eslint-disable-line
		}
		return arr2 as any[] & { length: TLength };
	}

	public static min(args: { source: any[], key?: string, defaultValue?: any }) {
		return this.maxOrMin(false, args);
	}

	public static max(args: { source: any[], key?: string, defaultValue?: any }) {
		return this.maxOrMin(true, args);
	}

	public static orderBy<T extends string | number | boolean>(arr: T[], orderType: Order = Order.Ascendend): T[] {
		if (!arr) {
			throw new NullReferenceError("'arr' should not be null");
		}

		if (arr.length === 0) {
			return arr;
		}

		let equal = 0;
		let greaterThan = orderType === Order.Ascendend ? 1 : orderType === Order.Descendend ? -1 : equal;
		let lessThan = orderType === Order.Ascendend ? -1 : orderType === Order.Descendend ? 1 : equal;

		// tslint:disable-next-line
		return arr.sort((a, b) => a > b ? greaterThan : a === b ? equal : lessThan);
	}

	public static orderByKey<T, TK extends string | number | boolean | Date>(arr: T[], fn: (item: T) => TK, orderType: Order = Order.Ascendend): T[] {
		if (!arr) {
			throw new NullReferenceError("'arr' should not be null");
		}

		if (arr.length === 0) {
			return arr;
		}

		let equal = 0;
		let greaterThan = orderType === Order.Ascendend ? 1 : orderType === Order.Descendend ? -1 : equal;
		let lessThan = orderType === Order.Ascendend ? -1 : orderType === Order.Descendend ? 1 : equal;

		// tslint:disable-next-line
		return arr.sort((a, b) => {
			let keyA = fn(a);
			let keyB = fn(b);
			return keyA > keyB ? greaterThan : keyA === keyB ? equal : lessThan;
		});
	}

	public static addIfNotExists<T>(arr: T[] | KnockoutObservableArray<T>, item: T): boolean {
		if (arr == null) {
			throw new InvalidParameterError("arr", InvalidParameterError.nullText);
		}

		let index = arr.indexOf(item);
		if (index !== -1) {
			return false;
		}

		arr.push(item);
		return true;
	}

	public static addByKeyIfNotExists<T, TKey extends keyof T>(arr: T[] | KnockoutObservableArray<T>, key: TKey, value: any, item: T): boolean {
		if (arr == null) {
			throw new InvalidParameterError("arr", InvalidParameterError.nullText);
		}

		let curArr: T[] = ko.isComputed(arr) ? arr() : arr;

		let foundItem = curArr.find((curItem) => curItem[key] === value);

		if (foundItem != null) {
			return false;
		}

		return ArrayHelper.addIfNotExists(arr, item);
	}

	public static remove<T>(arr: T[] | KnockoutObservableArray<T>, item: T): boolean {
		if (arr == null) {
			throw new InvalidParameterError("arr", InvalidParameterError.nullText);
		}
		let index = arr.indexOf(item);
		if (index === -1) {
			return false;
		}
		if (ko.isObservable(arr)) {
			arr.remove(item);
		} else {
			arr.splice(index, 1);
		}

		return true;
	}

	public static removeByKey<T, TKey extends keyof T>(arr: T[] | KnockoutObservableArray<T>, key: TKey, value: any): boolean {
		if (arr == null) {
			throw new InvalidParameterError("arr", InvalidParameterError.nullText);
		}
		let curArr: T[] = ko.isComputed(arr) ? arr() : arr;
		let foundItem = curArr.find((item) => item[key] === value);
		return ArrayHelper.remove(arr, foundItem);
	}

	public static groupBy<T, TKey extends keyof T>(arr: T[], key: TKey, orderType: Order = Order.Ascendend): Array<IGroupBy<T, TKey>> {
		if (arr == null) {
			throw new InvalidParameterError("arr", InvalidParameterError.nullText);
		}

		if (arr.length === 0) {
			return [];
		}

		let groupedItems: Array<IGroupBy<T, TKey>> = [];

		for (let item of arr) {
			// check if key exists
			if (!(key in item)) {
				throw new InvalidParameterError("key", "key not found in item");
			}
			let curKeyVal = item[key];
			let curGroupedItem: IGroupBy<T, TKey> = groupedItems.find((gi) => gi.key === curKeyVal);
			if (curGroupedItem == null) {
				// create item
				curGroupedItem = { key: curKeyVal, items: [] };
				groupedItems.push(curGroupedItem);
			}

			curGroupedItem.items.push(item);
		}

		return groupedItems.sort((a, b) => {
			let res = a.key < b.key ? -1 : a.key === b.key ? 0 : 1;
			switch (orderType) {
				case Order.Descendend:
					return res * (-1);
				default:
					return res;
			}
		});
	}

	public static distinct<T>(source: T[]): T[] {
		return source.filter((value, index, self) => self.indexOf(value) === index);
	}

	public static take<T>(source: T[], count: number): T[] {
		let result: T[] = [];
		if (!(source?.length > 0)) {
			return result;
		}

		if (source.length < count) {
			return source;
		}

		for (let i = 0; i < count; i++) {
			result.push(source[i]);
		}

		return result;
	}

	private static maxOrMin(isMax: boolean, args: { source: any[], key?: string, defaultValue?: any }) {
		let source = args.source;
		let key = args.key;

		if (!source || source.length === 0) {
			if (typeof args.defaultValue !== "undefined") {
				return args.defaultValue;
			}
			throw new ArgumentError("arrSource", "Array should not be null or empty");
		}

		let tmpArr = source;

		if (key) {
			if (Object.keys(tmpArr[0]).indexOf(key) === -1) {
				throw new KeyNotExistsError(key, KeyNotExistsError.keyNotExistsText);
			}
			tmpArr = source.map((item) => {
				if (ko.isObservable(item[key]) || ko.isComputed(item[key])) {
					return item[key]();
				}
				return item[key];
			});
		}

		let minMax = tmpArr[0];

		for (let item of tmpArr) {
			if (isMax && minMax < item || !isMax && minMax > item) {
				minMax = item;
			}
		}

		return minMax != null ? minMax : args.defaultValue;
	}
}